/**
 * Workspace Registry — the single declarative spine for Crucible's personas.
 *
 * One entry per persona/workspace. Routing, labels, canvas metadata, nav-rail
 * membership + presentation, persona pills, and handoff-eligibility all derive
 * from here, replacing ~9 scattered re-declarations
 * (see docs/internal/workspace-registry-design.md).
 *
 * ISOMORPHIC: no React, no next/navigation, no client-only imports — so the
 * chat-v3 route handler and node tests can import it. Icons are referenced by
 * lucide name and resolved in a client-only adapter.
 *
 * Tailwind v4 auto-scans this file, so the literal nav/pill class strings below
 * are picked up by the JIT (do NOT build them via template interpolation —
 * those would be purged).
 *
 * SCOPE NOTE (behavior-preserving): tool suppression + system prompts still
 * live in routing.ts MODE_CONFIGS. `toolGrants` is forward-looking docs for the
 * later additive-grants migration — it does NOT drive runtime tool selection
 * yet. Values here reproduce today's behavior exactly.
 */

export type WorkspaceId =
  | 'concierge'
  | 'study-design'
  | 'norma'
  | 'val'
  | 'testcases'
  | 'marq'
  | 'audrey'
  | 'compli'

/** Capability groups — vocabulary for the future additive-grants model. */
export type ToolGroup =
  | 'odmAuthoring' | 'odmRead' | 'codebooks' | 'crm'
  | 'canvas' | 'handoff' | 'research' | 'standards' | 'qualio'

export interface WorkspaceEntry {
  id: WorkspaceId
  /** Pathname prefixes that resolve to this workspace (startsWith match). */
  routes: readonly string[]
  /** Full display name (e.g. "Study Designer"). */
  label: string
  /** Short nav-rail label (e.g. "Study Design"). */
  navLabel: string
  /** LLM-facing context label (e.g. "Study Designer (ODM/CRF builder)"). */
  contextLabel: string
  /** lucide icon name; resolved in a client adapter (keeps this file RSC/test-safe). */
  iconName?: string
  /**
   * Canvas accent colour. Always `var(--persona-<id>)` — never a literal.
   * These used to be hardcoded here while globals.css defined a token for the
   * same workspace, and they drifted: Norma's pill was violet while her rail
   * icon was gold, 152 degrees apart, and Concierge's pill was green against
   * an orange rail.
   */
  accent?: string
  /** Does this workspace have its own chat persona (prompt + toolset)? */
  hasChatMode: boolean
  /** Forward-looking — additive tool grants. NOT wired to runtime yet. */
  toolGrants: readonly ToolGroup[]
  /** Canvas display name. */
  canvasName?: string
  /** Where canvas CONTENT lives ("Show me" target). */
  canvasHref?: string
  /** Persona landing zone ("Continue with X" target, canvas back-link). */
  workspaceHref: string
  /** Artifact tab IDs the canvas exposes. */
  canvasTabs: readonly string[]
  /** Shown in the MissionRail? */
  inNavRail: boolean
  /** Order within the rail. */
  navOrder?: number
  /** Nav-rail tooltip/subtitle (rail workspaces only). */
  navDescription?: string
  /** Nav-rail active-state Tailwind classes (rail workspaces only). */
  navAccent?: { bg: string; text: string }
  /** Short label for the canvas-library persona pill (pill consumers only). */
  pillLabel?: string
  /** Persona-pill Tailwind classes (pill consumers only). */
  pillClasses?: { text: string; bg: string }
  /** Production-ready → eligible as a handoff target. */
  ready: boolean
  /**
   * RBAC surface gate. Always `surface:<id>`. The held-cap set is checked
   * against this via hasCapability() to gate nav membership and route access.
   * (`member` holds `surface:concierge`; admins hold `*`.)
   */
  requiredCapability: `surface:${WorkspaceId}`
}

export const WORKSPACES: Record<WorkspaceId, WorkspaceEntry> = {
  concierge: {
    id: 'concierge',
    requiredCapability: 'surface:concierge',
    routes: ['/mainchat'],
    label: 'Concierge', navLabel: 'Concierge', contextLabel: 'Concierge (main chat)',
    iconName: 'MessageSquare', accent: 'var(--persona-concierge)',
    hasChatMode: true,
    toolGrants: ['canvas', 'handoff', 'odmRead', 'codebooks', 'research', 'standards'],
    canvasName: 'Concierge Canvas', canvasHref: '/mainchat/canvas', canvasTabs: ['__canvas__'],
    workspaceHref: '/mainchat', inNavRail: true, navOrder: 0, ready: true, // valid handoff target — the return-to-concierge path
    navDescription: 'Crucible Concierge',
    navAccent: { bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400' },
    pillLabel: 'Concierge',
    pillClasses: { text: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-500/15' },
  },
  'study-design': {
    id: 'study-design',
    requiredCapability: 'surface:study-design',
    routes: ['/tools/study-design', '/study-design'], // legacy alias preserved
    label: 'Study Designer', navLabel: 'Study Design', contextLabel: 'Study Designer (ODM/CRF builder)',
    iconName: 'FileCode', accent: 'var(--persona-stud)',
    hasChatMode: true,
    toolGrants: ['odmAuthoring', 'odmRead', 'codebooks', 'canvas', 'research', 'standards'],
    canvasName: 'Study Design Canvas', canvasHref: '/tools/study-design',
    canvasTabs: ['__build__', '__canvas__', '__xml__', '__form__', '__graph__', '__comparator__'],
    workspaceHref: '/tools/study-design', inNavRail: true, navOrder: 1, ready: true,
    navDescription: 'AI-native study builder',
    navAccent: { bg: 'bg-persona-stud/10', text: 'text-persona-stud' },
    pillLabel: 'Study Design',
    pillClasses: { text: 'text-persona-stud', bg: 'bg-persona-stud/15' },
  },
  val: {
    id: 'val',
    requiredCapability: 'surface:val',
    routes: ['/tools/val', '/tools/val/canvas'],
    label: 'Val', navLabel: 'Val', contextLabel: 'Val (ODM validation)',
    iconName: 'ClipboardCheck', accent: 'var(--persona-val)',
    hasChatMode: true,
    toolGrants: ['odmRead', 'codebooks', 'canvas', 'research', 'standards'],
    canvasName: 'Validation Canvas', canvasHref: '/tools/val/canvas',
    // Markdown authoring only — Comparator is Study Designer ODM-diff chrome.
    canvasTabs: ['__canvas__'],
    workspaceHref: '/tools/val', inNavRail: true, navOrder: 2, ready: false,
    navDescription: 'QA validation pipeline',
    navAccent: { bg: 'bg-violet-500/10', text: 'text-violet-700 dark:text-violet-400' },
    pillLabel: 'Val',
    pillClasses: { text: 'text-violet-700 dark:text-violet-300', bg: 'bg-violet-500/15' },
  },
  norma: {
    id: 'norma',
    requiredCapability: 'surface:norma',
    routes: ['/tools/codebooks'],
    label: 'Norma', navLabel: 'Norma', contextLabel: 'Norma (codebook management)',
    iconName: 'BookOpen', accent: 'var(--persona-norma)',
    hasChatMode: true,
    toolGrants: ['codebooks', 'canvas', 'research', 'standards'],
    canvasName: 'Codebook Canvas', canvasHref: '/tools/codebooks/canvas', canvasTabs: ['__canvas__'],
    workspaceHref: '/tools/codebooks', inNavRail: true, navOrder: 3, ready: true,
    navDescription: 'Codebook import wizard',
    navAccent: { bg: 'bg-amber-500/10', text: 'text-amber-700 dark:text-amber-400' },
    pillLabel: 'Norma',
    pillClasses: { text: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-500/15' },
  },
  marq: {
    id: 'marq',
    requiredCapability: 'surface:marq',
    routes: ['/tools/marq-ketting'],
    label: 'Marq Ketting', navLabel: 'Marq Ketting', contextLabel: 'Marq (business development)',
    iconName: 'BarChart3', accent: 'var(--persona-marq)',
    hasChatMode: true,
    toolGrants: ['crm', 'canvas', 'research', 'standards'],
    canvasName: 'BD Canvas', canvasHref: '/tools/marq-ketting/canvas', canvasTabs: ['__canvas__'],
    workspaceHref: '/tools/marq-ketting', inNavRail: true, navOrder: 4, ready: true,
    navDescription: 'BD discovery & therapy area intel',
    navAccent: { bg: 'bg-indigo-500/10', text: 'text-indigo-700 dark:text-indigo-400' },
    pillLabel: 'Marq',
    pillClasses: { text: 'text-indigo-700 dark:text-indigo-300', bg: 'bg-indigo-500/15' },
  },
  testcases: {
    // TODAY: still a declared chat mode (orphaned — /tools/test-cases renders a
    // structured TestCaseShell, not a chat). Behavior-preserving for now; the
    // hasChatMode→false flip + prompt deletion is a separate commit.
    id: 'testcases',
    requiredCapability: 'surface:testcases',
    routes: ['/tools/test-cases'],
    label: 'Test Cases', navLabel: 'Test Cases', contextLabel: 'Test Cases (generation tool)',
    iconName: 'ClipboardCheck', accent: 'var(--persona-testcases)',
    hasChatMode: true,
    toolGrants: ['odmRead', 'codebooks', 'canvas', 'research', 'standards'],
    canvasName: 'Test Cases Canvas', canvasHref: '/tools/test-cases', canvasTabs: ['__canvas__'],
    workspaceHref: '/tools/test-cases', inNavRail: false, ready: false,
    // not in nav rail; has a persona pill in the canvas library
    pillLabel: 'Test Cases',
    pillClasses: { text: 'text-slate-300', bg: 'bg-slate-500/15' },
  },
  audrey: {
    // Suppressed from nav rail until Audrey is redesigned as an audit-log persona.
    // Compli owns QMS document investigation today. Routes + code stay live for
    // direct URLs and a future re-enable (flip inNavRail back to true).
    id: 'audrey',
    requiredCapability: 'surface:audrey',
    routes: ['/tools/audrey-trail'],
    label: 'Audrey', navLabel: 'Audrey', contextLabel: 'Audrey (quality & compliance)',
    iconName: 'Scale', accent: 'var(--persona-audrey)',
    hasChatMode: false,
    toolGrants: [],
    canvasTabs: [],
    workspaceHref: '/tools/audrey-trail', inNavRail: false, navOrder: 5, ready: false,
    navDescription: 'Regulatory expert · submissions · audits · compliance',
    navAccent: { bg: 'bg-purple-500/10', text: 'text-purple-700 dark:text-purple-400' },
  },

  compli: {
    // The 4-step guided Compli workspace, surfaced as its own rail persona
    // (rather than folded into Audrey — fca's 7-step audrey-trail stays intact).
    // hasChatMode:true + the Compli prompt (modes/compli.ts) landed so canvas
    // writes from /tools/compli attribute to the Compli Canvas instead of
    // falling through to Concierge's — same ready:false + hasChatMode:true
    // staging already used by Val/Norma (unfinished persona, but functional
    // and not yet offered as a route_handoff target).
    // canvasHref is its own /canvas sub-route (Val/Norma/Marq/Concierge
    // pattern), not the bespoke /tools/compli page itself — that page has no
    // ToolCanvas, so a same-route href would silently strand canvas writes
    // (see KNOWN_GAPS in canvas-wiring.test.ts, now fixed for compli).
    id: 'compli',
    routes: ['/tools/compli'],
    label: 'Compli', navLabel: 'Compli', contextLabel: 'Compli (quality & compliance assessment)',
    iconName: 'AwardCheck', accent: 'var(--persona-compli)',
    requiredCapability: 'surface:compli',
    hasChatMode: true,
    toolGrants: ['canvas', 'research', 'standards', 'qualio'],
    canvasName: 'Compli Canvas', canvasHref: '/tools/compli/canvas', canvasTabs: ['__canvas__'],
    workspaceHref: '/tools/compli', inNavRail: true, navOrder: 6, ready: false,
    navDescription: 'Compliance assessment · ISO gaps · process maps · audits',
    navAccent: { bg: 'bg-teal-500/10', text: 'text-teal-700 dark:text-teal-400' },
  },
}

export const ENTRIES: readonly WorkspaceEntry[] = Object.values(WORKSPACES)

// ── Derived accessors (the scattered declarations collapse to these) ────────

/** Resolve the workspace for a pathname (first route prefix that matches). */
export function workspaceFromPathname(pathname: string | null): WorkspaceEntry {
  const hit = ENTRIES.find((w) => w.routes.some((r) => pathname?.startsWith(r)))
  return hit ?? WORKSPACES.concierge
}

/**
 * The chat mode a turn runs as. A non-chat workspace (testcases today; audrey
 * today) falls back to concierge — matching routing.ts:239 behavior.
 */
export function chatModeFromPathname(pathname: string | null): WorkspaceId {
  const w = workspaceFromPathname(pathname)
  return w.hasChatMode ? w.id : 'concierge'
}

/**
 * Handoff targets = every ready chat persona. Handoffs are any→any, INCLUDING
 * back to concierge (the return path). RouteHandoffCard self-suppresses when
 * the target is the user's current workspace, so a same-workspace card never
 * shows. Unfinished personas (val/testcases, audrey-until-shipped) stay
 * out via ready:false. Norma is ready and participates in handoffs.
 */
export const handoffTargets: readonly WorkspaceId[] = ENTRIES
  .filter((w) => w.ready && w.hasChatMode)
  .map((w) => w.id)

/** Runtime guard — JSON-schema enums are advisory; models often emit out-of-band values. */
export function isHandoffTarget(id: string | undefined | null): id is WorkspaceId {
  return !!id && (handoffTargets as readonly string[]).includes(id)
}

/** MissionRail items, in order. */
export const navItems: readonly WorkspaceEntry[] = ENTRIES
  .filter((w) => w.inNavRail)
  .sort((a, b) => (a.navOrder ?? 99) - (b.navOrder ?? 99))

/** Canvas metadata for a workspace (name + content href + landing href). */
export function canvasMetaFor(id: WorkspaceId): { name: string; href: string; workspaceHref: string } {
  const w = WORKSPACES[id]
  return {
    name: w.canvasName ?? w.label,
    href: w.canvasHref ?? w.workspaceHref,
    workspaceHref: w.workspaceHref,
  }
}

export const personaLabel = (id: WorkspaceId): string => WORKSPACES[id].label
export const contextLabelFor = (id: WorkspaceId): string => WORKSPACES[id].contextLabel
